import { FC } from 'react';
import styled from 'styled-components';
import {
  TranslatedItem,
  useTranslateItem,
} from 'src/hooks/directus/useTranslateItem';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { H2 } from 'src/components/common/typography/Header';
import { motion } from 'framer-motion';
import { makeTransition } from 'src/utils/motion/transitions';
import { PimImage } from 'src/components/common/PimImage';
import { Button, ButtonContainer } from 'src/components/common/Button';
import { NeauviaItem } from 'src/models/directus';
import { SwiperSlide } from 'swiper/react';
import { ProductsSwiper } from 'src/components/common/Swipers/ProductsSwiper';
import { useProductDetailsQuery } from 'src/modules/products/hooks/useProductDetailsQuery';
import { route } from 'src/services/route';
import { TrackedLink } from 'src/components/TrackedLink';
import { useMediaQuery } from 'src/hooks/useMediaQuery';

const Product = styled.a`
  ${media.w.lessThan.desktop} {
    display: grid;
    grid-template:
      'image name'
      'image description'
      'image link';

    grid-template-columns: 118px 1fr;
    grid-template-rows: auto 1fr auto;
    gap: 13px 19px;
    margin: 0 0 36px;
  }

  ${media.w.greaterThanOrEqual.desktop} {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 264px;
    gap: 17px;
    margin: 0 20px 0 0;
  }
`;

const ProductImage = styled.div`
  grid-area: image;
  position: relative;
  width: 104px;
  height: 108px;
  margin: 0 10px;

  img {
    object-fit: contain;
  }

  ${media.w.greaterThanOrEqual.desktop} {
    width: 229px;
    height: 229px;
    margin: 9px;
  }
`;

const ProductName = styled.div`
  grid-area: name;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.text.palette.primary};

  ${media.w.greaterThanOrEqual.desktop} {
    font-weight: 500;
    font-size: 16px;
    line-height: 18px;
    text-align: center;
  }
`;

const ProductDescription = styled.div`
  grid-area: description;
  font-weight: 300;
  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.text.palette.primary};

  ${media.w.greaterThanOrEqual.desktop} {
    font-weight: 300;
    font-size: 14px;
    line-height: 18px;
    margin-bottom: 18px;
    text-align: center;
    flex: 1;
  }
`;

const ProductLink = styled.div`
  grid-area: link;

  ${media.w.lessThan.desktop} {
    ${ButtonContainer} {
      min-width: 162px !important;
    }
  }
`;

const ProductBox = ({ code }: { code: string }) => {
  const { _ } = useTranslate();
  const translateFn = useTranslateItem();

  const { data: product } = useProductDetailsQuery(code);

  if (!product) {
    return null;
  }

  return (
    <TrackedLink href={route.products().page(product)} passHref target="_blank">
      <Product>
        <ProductImage>
          {product.image && (
            <PimImage src={product.image.id} sizes="30vw" layout="fill" />
          )}
        </ProductImage>
        <ProductName>{translateFn(product).name}</ProductName>
        <ProductDescription>
          <Markdown>{translateFn(product).description_short ?? ''}</Markdown>
        </ProductDescription>
        <ProductLink>
          <Button compound forwardedAs="span">
            {_('Read more')}
          </Button>
        </ProductLink>
      </Product>
    </TrackedLink>
  );
};

export const ProductsTab: FC<{
  item: TranslatedItem<NeauviaItem> | undefined;
}> = ({ item }) => {
  const { _ } = useTranslate();

  const isMobile = useMediaQuery(media.w.lessThan.desktop);

  const products = item && 'products' in item ? item.products ?? [] : null;

  if (!products) {
    return null;
  }

  return (
    <>
      <H2
        as={motion.h3}
        variants={makeTransition({
          fade: true,
          from: 'bottomMore',
        })}
      >
        {_('PRODUCTS')}
      </H2>

      <motion.div
        variants={makeTransition({
          fade: true,
          from: 'bottomMore',
        })}
      >
        <ProductsSwiper direction={isMobile ? 'vertical' : 'horizontal'}>
          {products.map((product) => (
            <SwiperSlide key={product.id} tag="li">
              <ProductBox code={product.products_id.pim_code} />
            </SwiperSlide>
          ))}
        </ProductsSwiper>
      </motion.div>
    </>
  );
};
